/**
 * Convert LaTeX to plain text format for backend
 */
export function latexToPlainText(latex: string): string {
  let result = latex;
  
  // Remove extra spaces
  result = result.trim();

  // Remove \left and \right first so \le in \left isn't treated as <=
  result = result.replace(/\\left|\\right/g, '');
  
  // Convert special symbols first (before processing fractions)
  result = result.replace(/\\pm/g, '±');
  result = result.replace(/\\le(?:q)?/g, '<=');
  result = result.replace(/\\ge(?:q)?/g, '>=');
  result = result.replace(/\\nexists/g, '∄');
  result = result.replace(/\\emptyset/g, '∅');
  result = result.replace(/\\varnothing/g, '∅');
  result = result.replace(/\\infty/g, '∞');
  
  // Convert square roots: \sqrt{x} -> (x)^(1/2) and \sqrt2 -> (2)^(1/2)
  // Use a special marker to protect 1/2 from being wrapped
  result = result.replace(/\\sqrt\{([^}]+)\}/g, '($1)^__SQRT_POWER__');
  result = result.replace(/\\sqrt([0-9a-zA-Z])/g, '($1)^__SQRT_POWER__');
  
  // Add implicit multiplication before sqrt power marker: 2(x)^__SQRT_POWER__ -> 2*(x)^__SQRT_POWER__
  result = result.replace(/([0-9a-zA-Z])\(([^)]+)\)\^__SQRT_POWER__/g, '$1*($2)^__SQRT_POWER__');
  
  // Convert shorthand fractions: \frac23 -> 2/3
  result = result.replace(/\\frac([0-9a-zA-Z])([0-9a-zA-Z])/g, '$1/$2');
  
  // Convert fractions: \frac{a}{b} -> (a)/(b), handle nested fractions
  // Need to do this iteratively to handle nested cases
  let maxIterations = 10;
  while (/\\frac\{/.test(result) && maxIterations-- > 0) {
    result = result.replace(/\\frac\{([^{}]*)\}\{([^{}]*)\}/g, '($1)/($2)');
  }
  
  // Now wrap standalone shorthand fractions: a/b -> (a)/(b)
  // But keep them simple inside braces or already inside parentheses
  result = result.replace(/(?<![\^\{\(])([0-9a-zA-Z])\/([0-9a-zA-Z])(?![\}\)])/g, '($1)/($2)');
  
  // Restore the sqrt power marker
  result = result.replace(/__SQRT_POWER__/g, '(1/2)');
  
  // Convert superscripts: x^{2} or x^2 -> x^2 or x^(complex)
  result = result.replace(/\^\{([^}]+)\}/g, (_match, exp) => {
    if (exp.length > 1 || /[+\-*/()\[\]]/.test(exp)) {
      return `^(${exp})`;
    }
    return `^${exp}`;
  });
  result = result.replace(/\^([0-9a-zA-Z])/g, '^$1');
  
  // Convert subscripts
  result = result.replace(/_\{([^}]+)\}/g, '_$1');
  result = result.replace(/_([0-9a-zA-Z])/g, '_$1');
  
  // Convert cdot and times to * and remove extra spaces
  result = result.replace(/\\cdot\s*/g, '*');
  result = result.replace(/\\times\s*/g, '*');
  
  // Remove any remaining backslashes
  result = result.replace(/\\/g, '');
  
  return result;
}
